import { isEmpty } from 'lodash';
import type { AddressObject, Attachment } from 'mailparser';

import type { DateTime } from './dateutils';
import type { InboundEmailAuthentication } from './inboundEmailUtils';

import { toConcatLowerCase } from './';

export const DEFAULT_DEBUG_MAIL_TO = 'it@aloalto.com';

export const UP2RENT_SUPPORT_EMAIL = 'support@up2rent.com';

export enum ResponseEmailType {
  UNKNOWN_RECEIVER = 'UNKNOWN_RECEIVER',
  UNKNOWN_TRANSMITTER = 'UNKNOWN_TRANSMITTER',
  ACK = 'ACK',
  ACK_NO_FILE = 'ACK_NO_FILE',
  REPLY = 'REPLY',
}

export enum AddressType {
  ISSUE = 'ISSUE',
  CHARGES = 'CHARGES',
}
export interface ParsedEmail {
  from: string;
  to: string;
  subject: string;
  date: DateTime;
  text: string;
  html: string;
  attachments: Attachment[];
  messageId?: string;
  references?: string | string[];
  envelopeRecipients?: string[];
  authentication?: InboundEmailAuthentication;
}

export const removeReplyFromEmail = (content: string) => {
  const regexList = [
    /^-*\s*(On\s.+\s.+\n?wrote:{0,1})\s{0,1}-*$/m, // On DATE, NAME <EMAIL> wrote:
    /^-*\s*(Le\s.+\s.+\n?écrit\s?:{0,1})\s{0,1}-*$/m, // Le DATE, NAME <EMAIL> a écrit :
    /^-*\s*(El\s.+\s.+\n?escribió:{0,1})\s{0,1}-*$/m, // El DATE, NAME <EMAIL> escribió:
    /^-*\s*(Il\s.+\s.+\n?scritto:{0,1})\s{0,1}-*$/m, // Il DATE, NAME <EMAIL> ha scritto:
    /^-*\s*(Em\s.+\s.+\n?escreveu:{0,1})\s{0,1}-*$/m, // Em DATE, NAME <EMAIL> ha escreveu:
    /^\s*(Am\s.+\s)\n?\n?schrieb.+\s?(\[|<).+(\]|>):$/m, // Am DATE schrieb NAME <EMAIL>:
    /^\s*(Op\s[\s\S]+?\n?schreef[\s\S]+:)$/m, // Il DATE, schreef NAME <EMAIL>:
    /^\s*((W\sdniu|Dnia)\s[\s\S]+?(pisze|napisał(\(a\))?):)$/mu, // W dniu DATE, NAME <EMAIL> pisze|napisał:
    /^\s*(Den\s.+\s\n?skrev\s.+:)$/m, // Den DATE skrev NAME <EMAIL>:
    /^\s*(pe\s.+\s.+\n?kirjoitti:)$/m, // pe DATE NAME <EMAIL> kirjoitti:
    /^\s*(Am\s.+\sum\s.+\s\n?schrieb\s.+:)$/m, // Am DATE um TIME schrieb NAME:
    /^(在[\s\S]+写道：)$/m, // > 在 DATE, TIME, NAME 写道：
    /^(20[0-9]{2}\..+\s작성:)$/m, // DATE TIME NAME 작성:
    /^(20[0-9]{2}\/.+のメッセージ:)$/m, // DATE TIME、NAME のメッセージ:
    /^(.+\s<.+>\sschrieb:)$/m, // NAME <EMAIL> schrieb:
    /^(.+\son.*at.*wrote:)$/m, // NAME on DATE wrote:
    // "From: NAME <EMAIL>" OR "From : NAME <EMAIL>" OR
    // "From : NAME<EMAIL>"(With support whitespace before start and before <)
    /^\s*(From\s?:.+\s?\n?\s*[\[|<].+[\]|>])/m,
    // "De: NAME <EMAIL>" OR "De : NAME <EMAIL>" OR
    // "De : NAME<EMAIL>"  (With support whitespace before start and before <)
    /^\s*(De\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m,
    // "Van: NAME <EMAIL>" OR "Van : NAME <EMAIL>" OR
    // "Van : NAME<EMAIL>"  (With support whitespace before start and before <)
    /^\s*(Van\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m,
    // "Da: NAME <EMAIL>" OR "Da : NAME <EMAIL>" OR
    // "Da : NAME<EMAIL>"  (With support whitespace before start and before <)
    /^\s*(Da\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m,
    /^(20[0-9]{2})-([0-9]{2}).([0-9]{2}).([0-9]{2}):([0-9]{2})\n?(.*)>:$/m, // 20YY-MM-DD HH:II GMT+01:00 NAME <EMAIL>:
    /^\s*([a-z]{3,4}\.\s[\s\S]+\sskrev\s[\s\S]+:)$/m, // DATE skrev NAME <EMAIL>:
    // DD.MM.20YY HH:II NAME <EMAIL>
    /^([0-9]{2}).([0-9]{2}).(20[0-9]{2})(.*)(([0-9]{2}).([0-9]{2}))(.*)\"( *)<(.*)>( *):$/m,
    /^[0-9]{2}:[0-9]{2}(.*)[0-9]{4}(.*)\"( *)<(.*)>( *):$/, // HH:II, DATE, NAME <EMAIL>:
    /^(.*)[0-9]{4}(.*)from(.*)<(.*)>:$/,
  ];

  for (const regex of regexList) {
    const match = content.match(regex);
    if (match) {
      return content.slice(0, match.index);
    }
  }
  return content;
};

export const parseMessageId = (rawEmail: string) => {
  const messageIdRegex = /Message-ID:\s*(<[^>]+>)/i;
  return rawEmail.match(messageIdRegex)?.[1];
};

export const parseReferences = (rawEmail: string) => {
  const referencesRegex = /References:\s*((?:<[^>]+>\s*)+)/i;
  const match = rawEmail.match(referencesRegex);

  if (match) {
    const referencesString = match[1];
    const referenceIdRegex = /<[^>]+>/g;
    const referenceIds = referencesString.match(referenceIdRegex);
    return referenceIds;
  }
  return undefined;
};

export const formatAddress = (address: AddressObject | undefined) => {
  return address?.text ?? '';
};

export const issueEmailSuffixGenerator = (name: string) => {
  const shortUuidGenerator = Math.random().toString(36).slice(2, 5);
  return `${toConcatLowerCase(name).substring(0, 5)}_${shortUuidGenerator}`;
};

export const getAddressTypeFromKey = async (key: string) => {
  if (key.includes('issues')) {
    return AddressType.ISSUE;
  }
  if (key.includes('charges')) {
    return AddressType.CHARGES;
  }
  return undefined;
};

// correspondent can be in the form of "John Doe <john@doe.com>" or "john@doe.com"
export const extractEmailOrNumber = (correspondent: string) => {
  const parts = correspondent.split('<');
  const email = parts.length > 1 ? parts[1].replace('>', '').trim() : parts[0].trim();
  return email;
};

export const removeSubPart = (recipient: string) => {
  if (!recipient.includes('@')) {
    return recipient;
  }
  const [address, domain] = recipient.toLocaleLowerCase().split('@');
  const addressWithoutSubaddress = address.split('+')[0];
  return `${addressWithoutSubaddress}@${domain}`;
};

// SES v2 accepts 40 MB per message after base64, which inflates by ~33%; 25 MB of
// attachments leaves room for the body, the inline logo and the headers.
export const MAX_SIZE_ATTACHMENT = 25 * 1024 * 1024;
export const MAX_SIZE_ATTACHMENT_IN_MB = MAX_SIZE_ATTACHMENT / 1024 / 1024;
export const MAX_SIZE_ATTACHMENT_TEXT = `(max: ${MAX_SIZE_ATTACHMENT_IN_MB} MB)`;
export const PDF_FILE_TYPE = 'application/pdf';

export const getDomainFromEmail = (email: string): string => email.substring(email.indexOf('@') + 1);

const MAILTO_PREFIX = /^mailto:/;
const EMAIL_DOMAIN_REGEX = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** The lower-cased domain of an e-mail domain or address (`mailto:` accepted), or null when it is not a domain. */
export const normaliseEmailDomain = (value: string): string | null => {
  const domain = getDomainFromEmail(value.trim().toLowerCase().replace(MAILTO_PREFIX, ''));
  return EMAIL_DOMAIN_REGEX.test(domain) ? domain : null;
};

export const removeDuplicateFromRecipient = (recipient: string) => {
  const splitRecipient = recipient.split(',');
  const uniqueRecipientParts: string[] = [];
  splitRecipient.forEach((recipientPart) => {
    const trimmedRecipientPart = recipientPart.trim();
    if (!isEmpty(trimmedRecipientPart) && !uniqueRecipientParts.includes(trimmedRecipientPart)) {
      uniqueRecipientParts.push(trimmedRecipientPart);
    }
  });
  return uniqueRecipientParts.join(', ');
};
